import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  addModuleSystem,
  removeModuleSystem,
} from "../../../store/features/moduleSlice";
import { POWER_PLANTS } from "../../../constants/powerPlants";
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import TonnageInput from "../../common/TonnageInput";

/**
 * Module Power Step Component
 * Handles power plant selection and configuration for modules
 */
export const ModulePowerStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleConfig = useAppSelector((state) => state.module.moduleConfig);
  const installedSystems = useAppSelector((state) => state.module.moduleConfig?.systems.powerPlant || []);

  // State for power plant configuration
  const [selectedPowerPlantType, setSelectedPowerPlantType] = useState<string>("");
  const [powerPlantSize, setPowerPlantSize] = useState<number>(1);

  if (!moduleConfig) {
    return <div className="text-gray-600 dark:text-gray-300">Loading module configuration...</div>;
  }

  // Get available power plants filtered by tech level
  const availablePowerPlants = Object.entries(POWER_PLANTS)
    .filter(([key, plant]) => plant.techLevel <= moduleConfig.techLevel)
    .map(([key, plant]) => ({
      key,
      ...plant
    }));

  // Get currently installed power plant
  const installedPowerPlant = installedSystems.length > 0 ? installedSystems[0] : null;

  const handleAddPowerPlant = () => {
    if (!selectedPowerPlantType) return;

    const powerPlant = POWER_PLANTS[selectedPowerPlantType as keyof typeof POWER_PLANTS];
    if (!powerPlant) return;

    // Calculate power plant stats based on size
    const power = powerPlant.powerPerTon * powerPlantSize;
    const cost = powerPlant.costPerTon * powerPlantSize * 1000000; // Convert MCr to Cr
    const tons = powerPlant.baseTons * powerPlantSize;

    dispatch(addModuleSystem({
      systemType: "powerPlant",
      system: {
        ...powerPlant,
        id: `${powerPlant.type}_${Date.now()}`,
        size: powerPlantSize,
        power: power,
        cost: cost,
        tons: tons,
        calculated: true,
      },
    }));

    // Reset form
    setSelectedPowerPlantType("");
    setPowerPlantSize(1);
  };

  const handleRemovePowerPlant = (powerPlantId: string) => {
    dispatch(removeModuleSystem({
      systemType: "powerPlant",
      systemId: powerPlantId,
    }));
  };

  const handlePowerPlantTypeChange = (type: string) => {
    setSelectedPowerPlantType(type);
    // Reset size when type changes
    setPowerPlantSize(1);
  };

  const handleSizeChange = (size: number) => {
    setPowerPlantSize(Math.max(1, size));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Module Power Plant Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Configure power generation for your module
        </p>
      </div>

      {/* Module Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
          Module Information
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Module Size:</span>
            <div className="text-blue-900 dark:text-blue-100">{formatNumber(moduleConfig.size)} tons</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Tech Level:</span>
            <div className="text-blue-900 dark:text-blue-100">TL {moduleConfig.techLevel}</div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Installed Power Plant:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {installedPowerPlant ? installedPowerPlant.name : "None"}
            </div>
          </div>
          <div>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Power Output:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {installedPowerPlant ? `${formatNumber(installedPowerPlant.power)} Power` : "0 Power"}
            </div>
          </div>
        </div>
      </div>

      {/* Installed Power Plants */}
      {installedPowerPlant && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
                🔧 Installed Power Plants
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">Type:</span>
                  <div className="text-green-900 dark:text-green-100">{installedPowerPlant.name}</div>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">Size:</span>
                  <div className="text-green-900 dark:text-green-100">{installedPowerPlant.size || 1}</div>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">Power:</span>
                  <div className="text-green-900 dark:text-green-100">{formatNumber(installedPowerPlant.power)} Power</div>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">Cost:</span>
                  <div className="text-green-900 dark:text-green-100">{formatCurrency(installedPowerPlant.cost)}</div>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">Tons:</span>
                  <div className="text-green-900 dark:text-green-100">{formatNumber(installedPowerPlant.tons)} tons</div>
                </div>
                <div>
                  <span className="text-green-700 dark:text-green-300 font-medium">TL:</span>
                  <div className="text-green-900 dark:text-green-100">{installedPowerPlant.techLevel}</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleRemovePowerPlant(installedPowerPlant.id)}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Power Plant Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Select Power Plant</h3>
        
        <div className="space-y-4">
          {/* Power Plant Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Power Plant Type
            </label>
            <select
              value={selectedPowerPlantType}
              onChange={(e) => handlePowerPlantTypeChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Select a power plant type...</option>
              {availablePowerPlants.map((plant) => (
                <option key={plant.key} value={plant.key}>
                  {plant.name} (TL {plant.techLevel}) - {plant.powerPerTon} Power/ton, {formatCurrency(plant.costPerTon * 1000000)} Cr/ton
                </option>
              ))}
            </select>
            {selectedPowerPlantType && (() => {
              const plant = POWER_PLANTS[selectedPowerPlantType as keyof typeof POWER_PLANTS];
              return plant ? (
                <p className="text-xs text-gray-600 mt-1">
                  {plant.description}
                </p>
              ) : null;
            })()}
          </div>

          {/* Power Plant Size Input */}
          {selectedPowerPlantType && (
            <div>
              <TonnageInput
                value={powerPlantSize}
                onChange={handleSizeChange}
                systemType="powerPlant"
                category="powerSystems"
                max={moduleConfig.size}
                label="Power Plant Size (tons)"
                placeholder="Enter power plant size in tons"
                className=""
                showValidation={true}
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum: {formatNumber(moduleConfig.size)} tons (module size)
              </p>
              {(() => {
                const plant = POWER_PLANTS[selectedPowerPlantType as keyof typeof POWER_PLANTS];
                if (!plant) return null;

                const power = plant.powerPerTon * powerPlantSize;
                const cost = plant.costPerTon * powerPlantSize * 1000000;

                return (
                  <div className="text-sm text-gray-700 mt-2 space-y-1">
                    <p><span className="font-medium">Power Output:</span> {formatNumber(power)} Power</p>
                    <p><span className="font-medium">Cost:</span> {formatCurrency(cost)}</p>
                  </div>
                );
              })()}
            </div>
          )}



          {/* Install Button */}
          {selectedPowerPlantType && (
            <div className="flex justify-end">
              <button
                onClick={handleAddPowerPlant}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Install Power Plant
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Footer with Power Stats */}
      {installedPowerPlant && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
            <div>
              <span className="text-gray-400 block text-sm">Power Plant</span>
              <span className="text-xl font-bold text-yellow-400">
                {installedPowerPlant.name}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Size</span>
              <span className="text-xl font-bold text-blue-400">
                {formatNumber(installedPowerPlant.tons)} tons
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Power Output</span>
              <span className="text-xl font-bold text-green-400">
                {formatNumber(installedPowerPlant.power)} Power
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Cost</span>
              <span className="text-xl font-bold text-purple-400">
                {formatCurrency(installedPowerPlant.cost)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
